// Was heute schon an Gemini-Modellen versucht wurde: pro Modell die Anzahl der
// Versuche, wie viele davon durchkamen, und welche HTTP-Status dabei auftraten.
//
// Zwei Leser:
//  - die Fallback-Kette in geminiConfig.js sortiert danach. Sie schliesst nie
//    ein Modell aus — ein gescheitertes Modell rutscht nur ans Ende, weil ein
//    Kontingent sich innerhalb des Tages wieder erholen kann.
//  - die Einstellungen zeigen den Tagesstand an.
//
// Bewusst localStorage und kein PocketBase: das Kontingent haengt am Browser
// des Admins genauso wie die Modellwahl (siehe getActiveGeminiModel), und ein
// Fehlversuch ist keine Information, die einen Serverschreibzugriff wert waere.

const STORAGE_KEY = 'hr_dashboard.gemini_attempts';

// Lokales Datum, nicht UTC: der Admin liest den Stand in seiner eigenen Zeit.
// Googles Free-Tier-Kontingent setzt nach Pazifikzeit zurueck, der Tageswechsel
// hier faellt also nicht mit dem des Kontingents zusammen. Das ist verkraftbar,
// weil das Protokoll nur sortiert und nie ausschliesst.
export const todayKey = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const emptyLog = (day) => ({ day, models: {} });

const read = (now = new Date()) => {
  const day = todayKey(now);
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return emptyLog(day);
    const parsed = JSON.parse(raw);
    // Ein alter Tag wird verworfen statt angezeigt — "heute" muss heute heissen.
    if (!parsed || parsed.day !== day || typeof parsed.models !== 'object') return emptyLog(day);
    return { day, models: parsed.models || {} };
  } catch (e) {
    // Unlesbar oder localStorage gesperrt (Privatmodus): als leer behandeln.
    return emptyLog(day);
  }
};

const write = (log) => {
  try {
    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch (e) {
    // Speicher voll oder gesperrt — das Protokoll ist Beiwerk, kein Grund
    // den laufenden Aufruf scheitern zu lassen.
  }
};

/**
 * Haelt einen Versuch fest. `status` ist der HTTP-Status, oder null wenn die
 * Anfrage den Server gar nicht erreicht hat (Netzfehler, Abbruch, Timeout).
 */
export const recordAttempt = (model, { status = null, ok = false, reason = '' } = {}, now = new Date()) => {
  const key = String(model || '').trim();
  if (!key) return;

  const log = read(now);
  const entry = log.models[key] || { attempts: 0, ok: 0, failed: 0, byStatus: {}, lastStatus: null, lastReason: '', lastAt: null };

  entry.attempts += 1;
  if (ok) entry.ok += 1;
  else entry.failed += 1;

  const statusKey = status == null ? reason || 'netzwerk' : String(status);
  entry.byStatus[statusKey] = (entry.byStatus[statusKey] || 0) + 1;
  entry.lastStatus = status;
  entry.lastReason = reason || '';
  entry.lastAt = now.toISOString();

  log.models[key] = entry;
  write(log);

  emit({ type: 'result', model: key, status, ok, reason });
};

/**
 * Der Tagesstand, fuer die Anzeige. Zuletzt benutzte Modelle zuerst.
 */
export const readTodayLog = (now = new Date()) => {
  const log = read(now);
  const models = Object.entries(log.models)
    .map(([model, entry]) => ({ model, ...entry }))
    .sort((a, b) => String(b.lastAt || '').localeCompare(String(a.lastAt || '')));
  return { day: log.day, models };
};

/**
 * Bringt die Kandidatenliste in die Reihenfolge, in der sie heute probiert
 * werden sollte. Kein Modell faellt raus — die Liste wird nur umsortiert:
 *
 *   1. was heute schon erfolgreich war (das laeuft gerade)
 *   2. was heute noch gar nicht dran war
 *   3. was heute gescheitert ist, am laengsten zurueckliegender Fehlschlag zuerst
 */
export const rankModels = (models = [], now = new Date()) => {
  const log = read(now);
  const bucket = (model) => {
    const entry = log.models[model];
    if (!entry) return 1;
    return entry.ok > 0 ? 0 : 2;
  };
  return [...models].sort((a, b) => {
    const diff = bucket(a) - bucket(b);
    if (diff !== 0) return diff;
    // Innerhalb der Fehlschlaege: der aelteste Fehlversuch hatte am meisten
    // Zeit, sich zu erholen.
    const aAt = log.models[a]?.lastAt || '';
    const bAt = log.models[b]?.lastAt || '';
    return String(aAt).localeCompare(String(bAt));
  });
};

// Mithoeren, waehrend die Kette laeuft. Das Tagesprotokoll allein beantwortet
// "was war heute", aber nicht "was passiert gerade" — und genau das muss die
// Fortschrittsanzeige zeigen, sonst sieht ein Durchlauf ueber acht Modelle aus
// wie ein einzelner Fehlschlag.
const listeners = new Set();

export const subscribeToAttempts = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const emit = (event) => {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch (e) {
      // Ein kaputter Zuhoerer darf den laufenden Aufruf nicht mitreissen.
    }
  });
};

/** Ein Versuch beginnt. Das Ergebnis folgt ueber recordAttempt. */
export const notifyAttemptStart = (model) => emit({ type: 'start', model: String(model) });

export const clearTodayLog = () => {
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch (e) {
    // s. o.
  }
};

export const GEMINI_ATTEMPT_LOG_STORAGE_KEY = STORAGE_KEY;
